/**
 * Is a date the analyzer read off a receipt believable enough to book on?
 *
 * <p>
 * The model reads a date from a photograph, so it can be wrong in ways a human never is —
 * a smudged year, a cropped corner, a "2026" read as "2020". Two of those are checkable
 * without knowing anything about the receipt: a receipt cannot be dated in the future, and
 * one dated years back is a misread rather than a farmer's filing habit.
 * </p>
 *
 * <p>
 * Checked HERE as well as on the server, and deliberately: the server refuses an
 * implausible date with a 400, which is right for a write path but wrong as the visitor's
 * experience — a scan at the booth must not fail because the model misread a year. The
 * client simply declines to send it, and the entry books today as it always did. The
 * server check remains as the backstop for anything not coming from this screen.
 * </p>
 *
 * <p>
 * Kept in step with `DemoEndpoints.MaxReceiptAgeYears`. Two years is generous on purpose:
 * a farmer scanning a shoebox at tax time legitimately has last year's receipts, and a
 * December one photographed in January must still book where it belongs.
 * </p>
 */
const MAX_RECEIPT_AGE_YEARS = 2;

export function isPlausibleReceiptDate(iso: string | null, today = new Date()): boolean {
  if (!iso) return false;

  // Parsed as UTC midnight rather than through `new Date(iso)`'s local-time path, so a
  // receipt dated today does not read as "tomorrow" for anyone east of UTC.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return false;
  const [, y, m, d] = match;
  const value = Date.UTC(Number(y), Number(m) - 1, Number(d));
  if (Number.isNaN(value)) return false;

  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  if (value > todayUtc) return false;

  const floor = Date.UTC(
    today.getUTCFullYear() - MAX_RECEIPT_AGE_YEARS,
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  return value >= floor;
}

/** "2026-08-25" -> "25 Aug 2026", for showing the reader which day it will book on. */
export function formatReceiptDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * The window a receipt date may fall in, as `YYYY-MM-DD` — for a native date input's
 * `min`/`max`.
 *
 * DERIVED FROM THE SAME CONSTANT `isPlausibleReceiptDate` uses, deliberately. A control
 * that lets somebody pick a day the validator above will then silently drop would be worse
 * than no control: the screen would say "it will book on that day" and it would not. Two
 * hand-written copies of "two years" is how those two answers start to disagree.
 *
 * These are a COURTESY, not the rule. The browser refuses out-of-range values on the
 * picker, `isPlausibleReceiptDate` refuses them on the way out, and
 * `DemoEndpoints.MaxReceiptAgeYears` refuses them on the way in. The last one is the rule.
 */
export function receiptDateBounds(today = new Date()): { min: string; max: string } {
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const max = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const min = new Date(
    Date.UTC(today.getUTCFullYear() - MAX_RECEIPT_AGE_YEARS, today.getUTCMonth(), today.getUTCDate()),
  );
  return { min: iso(min), max: iso(max) };
}
